import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import type { MapPin } from './MapView';
import { calculateDistance } from '@/services/location';

type DailyDistanceSummaryProps = {
  itineraryItems?: ItineraryItem[];
  pins: MapPin[];
  referencePointName?: string;
  compact?: boolean;
};

type DailySummary = {
  dayKey: string;
  dateLabel: string;
  totalDistanceMeters: number;
  stopCount: number;
  walkableLegs: number;
  totalLegs: number;
  longestLeg?: {
    from: string;
    to: string;
    distanceLabel: string;
  };
};

const pad2 = (n: number) => String(n).padStart(2, '0');

// IMPORTANT: use a **local** day key (not UTC `toISOString()`), otherwise users can see an off-by-one day.
const getLocalDayKey = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const parseLocalDayKey = (dayKey: string) => {
  const [y, m, d] = dayKey.split('-').map((v) => parseInt(v, 10));
  // Local midnight (prevents UTC parsing shifting the date)
  return new Date(y, (m || 1) - 1, d || 1);
};

const formatDistanceLabel = (meters: number) =>
  meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;

export const DailyDistanceSummary: React.FC<DailyDistanceSummaryProps> = ({
  itineraryItems = [],
  pins,
  referencePointName,
  compact = false,
}) => {
  const theme = useTheme();

  const summary = useMemo<DailySummary | null>(() => {
    if (!itineraryItems.length) {
      return null;
    }

    const pinLookup = new Map<string, MapPin>();
    pins.forEach((pin) => {
      if (pin.type === 'itinerary') {
        pinLookup.set(pin.id, pin);
        const dataItem = pin.data as ItineraryItem | undefined;
        if (dataItem?.id) {
          pinLookup.set(dataItem.id, pin);
        }
      }
    });

    const groupedByDay = itineraryItems.reduce<Record<string, ItineraryItem[]>>((acc, item) => {
      if (!item.startDate) {
        return acc;
      }
      const date = item.startDate instanceof Date ? item.startDate : new Date(item.startDate as any);
      const key = getLocalDayKey(date);
      acc[key] = acc[key] ? [...acc[key], item] : [item];
      return acc;
    }, {});

    const todayKey = getLocalDayKey(new Date());
    const sortedKeys = Object.keys(groupedByDay).sort();
    const targetKey =
      sortedKeys.find((key) => key >= todayKey) ?? sortedKeys[sortedKeys.length - 1] ?? null;

    if (!targetKey) {
      return null;
    }

    const getStartMs = (item: ItineraryItem) => {
      const base =
        item.startDate
          ? new Date(item.startDate as any)
          : parseLocalDayKey(targetKey);
      const startTime = (item.startTime ?? '').trim();
      if (startTime && startTime !== 'flexible' && /^\d{1,2}:\d{2}/.test(startTime)) {
        const [h, m] = startTime.split(':').map((v) => parseInt(v, 10));
        if (!Number.isNaN(h)) {
          base.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
        }
      } else {
        base.setHours(0, 0, 0, 0);
      }
      return base.getTime();
    };

    const itemsForDay = groupedByDay[targetKey]
      .slice()
      .sort((a, b) => getStartMs(a) - getStartMs(b));

    if (itemsForDay.length < 2) {
      return null;
    }

    const legs = [];
    for (let i = 0; i < itemsForDay.length - 1; i++) {
      const current = itemsForDay[i];
      const next = itemsForDay[i + 1];
      const currentPin = pinLookup.get(current.id);
      const nextPin = pinLookup.get(next.id);

      if (!currentPin?.coordinates || !nextPin?.coordinates) {
        continue;
      }

      const distance = calculateDistance(currentPin.coordinates, nextPin.coordinates);
      legs.push({
        distance,
        from: current.title,
        to: next.title,
      });
    }

    if (!legs.length) {
      return null;
    }

    const totalDistanceMeters = legs.reduce((sum, leg) => sum + leg.distance, 0);
    const walkableLegs = legs.filter((leg) => leg.distance <= 1200).length;
    const longestLeg = legs.reduce<typeof legs[number] | null>(
      (longest, leg) => (longest && longest.distance >= leg.distance ? longest : leg),
      null,
    );

    const dateLabel = parseLocalDayKey(targetKey).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    return {
      dayKey: targetKey,
      dateLabel,
      totalDistanceMeters,
      stopCount: itemsForDay.length,
      walkableLegs,
      totalLegs: legs.length,
      longestLeg:
        longestLeg && longestLeg.distance > 0
          ? {
              from: longestLeg.from,
              to: longestLeg.to,
              distanceLabel: formatDistanceLabel(longestLeg.distance),
            }
          : undefined,
    };
  }, [itineraryItems, pins]);

  if (!summary) {
    return null;
  }

  const totalDistanceKm = (summary.totalDistanceMeters / 1000).toFixed(1);
  const walkabilityLabel = (() => {
    if (!summary.totalLegs) return '—';
    const ratio = summary.walkableLegs / summary.totalLegs;
    if (ratio >= 0.7) return 'High';
    if (ratio >= 0.4) return 'Medium';
    return 'Low';
  })();
  const walkabilityRatio = summary.totalLegs ? Math.max(0, Math.min(1, summary.walkableLegs / summary.totalLegs)) : 0;
  const walkabilityColor =
    walkabilityLabel === 'High'
      ? theme.colors.success
      : walkabilityLabel === 'Medium'
        ? (theme.colors.warning || theme.colors.primary)
        : theme.colors.textSecondary;

  const headerLabel =
    summary.dayKey === getLocalDayKey(new Date()) ? 'Today' : `Next up • ${summary.dateLabel}`;

  const walkabilityCaption = compact
    ? `Walk${summary.totalLegs ? ` • ${summary.walkableLegs}/${summary.totalLegs}` : ''}`
    : `Walkability${summary.totalLegs ? ` • ${summary.walkableLegs}/${summary.totalLegs}` : ''}`;

  return (
    <Card
      style={[
        styles.card,
        compact && styles.cardCompact,
        {
          backgroundColor: theme.colorScheme === 'dark'
            ? theme.colors.surface + 'B3'
            : theme.colors.surface + 'C8',
        },
      ]}
    >
      <View style={styles.cardHeader}>
        <Caption color={theme.colors.textSecondary} numberOfLines={1} ellipsizeMode="tail">
          {headerLabel}
        </Caption>
        {referencePointName && (
          <Caption
            color={theme.colors.textSecondary}
            numberOfLines={1}
            ellipsizeMode="tail"
            style={compact && styles.anchorCompact}
          >
            Anchor: {referencePointName}
          </Caption>
        )}
      </View>
      <View style={[styles.metricsRow, compact && styles.metricsRowCompact]}>
        <View style={[styles.metric, compact && styles.metricCompact]}>
          <ThemedText variant="h3" style={[styles.metricValue, compact && styles.metricValueCompact]}>
            {totalDistanceKm}
          </ThemedText>
          <Caption color={theme.colors.textSecondary} numberOfLines={1} ellipsizeMode="tail">
            km travel
          </Caption>
        </View>
        <View style={[styles.metric, compact && styles.metricCompact]}>
          <ThemedText variant="h3" style={[styles.metricValue, compact && styles.metricValueCompact]}>
            {summary.stopCount}
          </ThemedText>
          <Caption color={theme.colors.textSecondary} numberOfLines={1} ellipsizeMode="tail">
            Stops
          </Caption>
        </View>
        <View style={[styles.metric, compact && styles.metricCompact]}>
          <ThemedText variant="h3" style={[styles.metricValue, compact && styles.metricValueCompact]}>
            {walkabilityLabel}
          </ThemedText>
          <Caption color={theme.colors.textSecondary} numberOfLines={1} ellipsizeMode="tail">
            {walkabilityCaption}
          </Caption>
          <View style={[styles.walkabilityTrack, compact && styles.walkabilityTrackCompact]}>
            <View style={[styles.walkabilityFill, { width: `${Math.round(walkabilityRatio * 100)}%`, backgroundColor: walkabilityColor }]} />
          </View>
        </View>
      </View>
      {!compact && summary.longestLeg && (
        <Caption color={theme.colors.textSecondary} style={styles.longestLegText} numberOfLines={2} ellipsizeMode="tail">
          Longest leg: {summary.longestLeg.distanceLabel} between {summary.longestLeg.from} → {summary.longestLeg.to}
        </Caption>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 8,
    padding: 12,
    borderRadius: 16,
    gap: 10,
  },
  cardCompact: {
    padding: 10,
    gap: 8,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  anchorCompact: {
    maxWidth: 120,
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  metricsRowCompact: {
    flexWrap: 'wrap',
    gap: 10,
  },
  metric: {
    alignItems: 'flex-start',
    gap: 4,
  },
  metricCompact: {
    flexGrow: 1,
    flexBasis: '30%',
  },
  metricValue: {
    fontWeight: '700',
  },
  metricValueCompact: {
    fontSize: 22,
    lineHeight: 24,
  },
  longestLegText: {
    marginTop: 4,
  },
  walkabilityTrack: {
    width: 72,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.08)',
    overflow: 'hidden',
    marginTop: 2,
  },
  walkabilityTrackCompact: {
    width: 64,
  },
  walkabilityFill: {
    height: '100%',
    borderRadius: 999,
  },
});

